import React, { useRef, useState } from 'react';
import { X, Download, Loader2 } from 'lucide-react';
import { downloadFormativeMarksheetPdf } from '../utils/marksheetPdf';

const FormativeMarksheetModal = ({
  isOpen,
  onClose,
  institution,
  facultyName,
  departmentName,
  programName,
  unitName,
  unitCode,
  semesterName,
  academicYear,
  schedules = [],
  students = []
}) => {
  const printRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);

  if (!isOpen) return null;

  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    try {
      await downloadFormativeMarksheetPdf({
        institution,
        facultyName,
        departmentName,
        programName,
        unitName,
        unitCode,
        semesterName,
        academicYear,
        schedules,
        students
      });
    } catch (err) {
      console.error('Error generating marksheet PDF:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Helper to compute raw total marks
  const computeRawTotal = (item) => {
    if (!item?.marks) return 0;
    const sum = Object.values(item.marks).reduce((acc, curr) => acc + (parseFloat(curr) || 0), 0);
    return Math.round(sum * 10) / 10;
  };

  // Helper to compute converted total (/60) as a whole number
  const computeConvertedTotal = (item) => {
    if (!item?.marks || schedules.length === 0) return 0;
    const rawSum = computeRawTotal(item);
    const maxRaw = schedules.length * 30;
    if (maxRaw <= 0) return 0;
    return Math.round((rawSum / maxRaw) * 60);
  };

  const semesterHeading = semesterName 
    ? `${semesterName.toUpperCase().replace(/SEMESTER/i, '').trim()} SEMESTER EXAMINATIONS`
    : 'SEMESTER EXAMINATIONS';

  // Export as CSV (No Grade, No Remarks, exact columns)
  const handleDownloadCsv = () => {
    const headers = [
      'Serial No#',
      'Name Of Student',
      'REG NO',
      ...schedules.map(s => `${s.title || 'CAM'} (/30)`),
      'Total (/60)'
    ];

    const rows = students.map((student, idx) => {
      const convertedScore = computeConvertedTotal(student);
      const camMarks = schedules.map(s => {
        const mark = student.marks?.[s.id];
        const poe = student.poe?.[s.id] || 'not_uploaded';
        let displayVal = mark !== undefined && mark !== null && mark !== '' ? Number(mark).toFixed(2) : '0.00';
        
        if (poe === 'not_uploaded') displayVal = 'NP';
        else if (poe === 'rejected' || poe === 'awaiting_trainer') displayVal = 'NA';

        return displayVal;
      });

      return [
        idx + 1,
        `"${(student.student?.full_name || '').replace(/"/g, '""')}"`,
        `"${student.student?.student_id || ''}"`,
        ...camMarks,
        convertedScore
      ];
    });

    const meta = [
      [`"${(institution?.name || 'UNIVERSITY OF EMBU').replace(/"/g, '""')}"`],
      [`"${(facultyName || '').replace(/"/g, '""')}"`],
      [`"${(departmentName || '').replace(/"/g, '""')}"`],
      ['"FORMATIVE MARKSHEET"'],
      [`"${semesterHeading}"`],
      [`"${academicYear || ''}"`],
      [`"${(programName || '').replace(/"/g, '""')}"`],
      [`"${(unitName ? `${unitName} - ` : '') + (unitCode || '')}"`],
      []
    ];

    const csvContent = [
      ...meta.map(m => m.join(',')),
      headers.join(','),
      ...rows.map(r => r.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `Formative_Marksheet_${unitCode || 'Unit'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[100] overflow-y-auto">
      
      {/* Landscape Container */}
      <div className="bg-white rounded-lg shadow-2xl max-w-[98vw] xl:max-w-[1300px] w-full my-auto overflow-hidden flex flex-col max-h-[96vh]">
        
        {/* Modal Action Bar */}
        <div className="bg-slate-800 text-white px-6 py-3 flex justify-between items-center flex-shrink-0">
          <div className="flex items-center gap-2">
            <Download className="h-5 w-5 text-blue-400" />
            <h3 className="font-semibold text-sm sm:text-base">Formative Marksheet Preview</h3>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownloadPdf}
              disabled={isDownloading}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-wait text-white px-5 py-2 rounded text-sm font-semibold flex items-center gap-2 shadow-sm transition-colors"
              title="Download Marksheet as PDF"
            >
              {isDownloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              <span>{isDownloading ? 'Generating...' : 'Download Marksheet'}</span>
            </button>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white p-1 rounded transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Printable Document Container */}
        <div className="p-4 sm:p-6 overflow-y-auto bg-slate-100 flex-1 flex justify-center">
          
          {/* Official A4 Landscape Sheet: 297mm width × 210mm height */}
          <div 
            ref={printRef}
            id="printable-marksheet-area"
            className="bg-white text-black p-6 sm:p-8 w-full max-w-[297mm] min-h-[200mm] shadow-lg border border-slate-200 print:border-none print:shadow-none print:m-0 print:p-0"
            style={{ fontFamily: '"Times New Roman", Times, serif' }}
          >
            {/* Header Section */}
            <div className="text-center mb-4">
              
              {/* Institution Crest / Logo */}
              {institution?.logo_url ? (
                <img 
                  src={institution.logo_url} 
                  alt="Institution Crest" 
                  className="h-16 sm:h-18 w-auto mx-auto mb-3 object-contain"
                />
              ) : (
                <div className="h-14 w-14 mx-auto mb-3 flex items-center justify-center border-2 border-slate-800 rounded-full">
                  <span className="font-bold text-[10px]">LOGO</span>
                </div>
              )}

              {/* Institution Name */}
              <h1 className="text-base sm:text-lg font-bold uppercase tracking-wide text-black leading-tight mb-1.5">
                {institution?.name || 'UNIVERSITY OF EMBU'}
              </h1>

              {/* Faculty Name (Replaces TVET Institute) */}
              {facultyName && (
                <h2 className="text-sm sm:text-base font-bold text-black uppercase leading-tight mb-1.5">
                  {facultyName}
                </h2>
              )}

              {/* Department Name */}
              {departmentName && (
                <h3 className="text-xs sm:text-sm font-semibold text-black uppercase leading-tight mb-0.5">
                  {departmentName}
                </h3>
              )}

              {/* Marksheet Title */}
              <h4 className="text-xs sm:text-sm font-bold text-black uppercase underline tracking-wider leading-tight mb-0.5">
                FORMATIVE MARKSHEET
              </h4>

              {/* Semester Examinations */}
              <p className="text-xs sm:text-sm font-bold text-black uppercase underline leading-tight mb-0.5">
                {semesterHeading}
              </p>

              {/* Academic Year */}
              {academicYear && (
                <p className="text-xs sm:text-sm font-bold text-black underline leading-tight mb-0.5">
                  {academicYear}
                </p>
              )}

              {/* Program Name */}
              {programName && (
                <p className="text-xs sm:text-sm font-bold text-black uppercase underline leading-tight mb-0.5">
                  {programName}
                </p>
              )}

              {/* Unit Code & Unit Name */}
              <p className="text-xs sm:text-sm font-bold text-black uppercase tracking-wide leading-tight mt-0.5">
                {unitName ? `${unitName} - ` : ''}{unitCode || ''}
              </p>
            </div>

            {/* Separator Line */}
            <div className="border-t-2 border-black mb-3"></div>

            {/* Marksheet Table - Landscape, Compact Font, No Text Wrap */}
            <div className="w-full overflow-x-auto">
              <table className="w-full border-collapse border border-black text-xs">
                <thead>
                  <tr className="border-b-2 border-black bg-slate-50 font-bold">
                    <th className="border border-black py-1.5 px-3 text-center whitespace-nowrap w-12">
                      Serial No#
                    </th>
                    <th className="border border-black py-1.5 px-4 text-left whitespace-nowrap">
                      Name Of Student
                    </th>
                    <th className="border border-black py-1.5 px-4 text-left whitespace-nowrap">
                      REG NO
                    </th>
                    {/* Dynamic CAM columns administered */}
                    {schedules.map(sched => (
                      <th key={sched.id} className="border border-black py-1.5 px-3 text-center whitespace-nowrap">
                        {sched.title || 'CAM'} (/30)
                      </th>
                    ))}
                    {/* Total Formative (/60) */}
                    <th className="border border-black py-1.5 px-4 text-center font-bold whitespace-nowrap">
                      Total (/60)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {students.length > 0 ? (
                    students.map((student, idx) => {
                      const convertedScore = computeConvertedTotal(student);

                      return (
                        <tr key={student.id || idx} className="border-b border-black hover:bg-slate-50">
                          <td className="border border-black py-1 px-3 text-center font-medium whitespace-nowrap">
                            {idx + 1}
                          </td>
                          <td className="border border-black py-1 px-4 text-left font-medium whitespace-nowrap">
                            {student.student?.full_name || 'Unknown student'}
                          </td>
                          <td className="border border-black py-1 px-4 text-left font-medium text-blue-900 print:text-black whitespace-nowrap">
                            {student.student?.student_id || '-'}
                          </td>
                          {schedules.map(sched => {
                            const mark = student.marks?.[sched.id];
                            const poe = student.poe?.[sched.id] || 'not_uploaded';
                            let displayVal = mark !== undefined && mark !== null && mark !== '' ? Number(mark).toFixed(2) : '0.00';
                            
                            let textClass = '';
                            if (poe === 'not_uploaded') {
                              displayVal = 'NP';
                              textClass = 'text-red-600 font-bold';
                            } else if (poe === 'rejected' || poe === 'awaiting_trainer') {
                              displayVal = 'NA';
                              textClass = 'text-amber-600 font-bold';
                            }

                            return (
                              <td key={sched.id} className={`border border-black py-1 px-3 text-center font-medium whitespace-nowrap ${textClass}`}>
                                {displayVal}
                              </td>
                            );
                          })}
                          <td className="border border-black py-1 px-4 text-center font-bold whitespace-nowrap">
                            {convertedScore}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td 
                        colSpan={3 + schedules.length + 1} 
                        className="border border-black py-4 text-center text-slate-500 italic whitespace-nowrap"
                      >
                        No enrolled student records found for this unit.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Key/Legend */}
            <div className="mt-4 text-xs text-black font-medium">
              <span className="font-bold underline">KEY:</span>
              <span className="ml-3"><strong className="text-red-600">NP</strong> - Not Present / POE Not Uploaded</span>
              <span className="ml-4"><strong className="text-amber-600">NA</strong> - POE Not Approved by Trainer</span>
            </div>

            {/* Official Signatures Section - 3 Landscape Columns */}
            <div className="mt-12 pt-6 border-t border-slate-300 grid grid-cols-3 gap-8 text-xs text-black">
              <div>
                <p className="font-bold uppercase mb-1">Trainer / Lecturer:</p>
                <div className="border-b border-black w-full my-5"></div>
                <p className="text-[11px]">Name, Signature & Date</p>
              </div>
              <div>
                <p className="font-bold uppercase mb-1">Head of Department:</p>
                <div className="border-b border-black w-full my-5"></div>
                <p className="text-[11px]">Name, Signature & Date</p>
              </div>
              <div>
                <p className="font-bold uppercase mb-1">External Verifier:</p>
                <div className="border-b border-black w-full my-5"></div>
                <p className="text-[11px]">Name, Signature & Date</p>
              </div>
            </div>

            {/* Verification Footer */}
            <div className="mt-6 text-[10px] text-slate-500 text-center flex justify-between border-t border-slate-200 pt-1.5">
              <span>Formative Continuous Assessment Marksheet</span>
              <span>Generated on {new Date().toLocaleDateString()}</span>
            </div>

          </div>
        </div>

      </div>

      {/* Landscape Print CSS Rules */}
      <style>{`
        @page {
          size: A4 landscape;
          margin: 10mm 12mm;
        }
        @media print {
          html, body {
            width: 100% !important;
            height: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          body * {
            visibility: hidden;
          }
          #printable-marksheet-area, #printable-marksheet-area * {
            visibility: visible;
          }
          #printable-marksheet-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            border: none !important;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>

    </div>
  );
};

export default FormativeMarksheetModal;
